import { ReactComponent as BatterySaverIcon } from '@features/regional-overview/assets/digital-traps/battery_saver.svg';
import { ReactComponent as BuildCircleIcon } from '@features/regional-overview/assets/digital-traps/build_circle.svg';
import { ReactComponent as PheromoneIcon } from '@features/regional-overview/assets/digital-traps/pheromone.svg';
import { ReactComponent as RotateRightIcon } from '@features/regional-overview/assets/digital-traps/rotate_right.svg';
import { ReactComponent as WifiIcon } from '@features/regional-overview/assets/digital-traps/wifi.svg';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import type { Status } from './status-tag.model';
import { StatusTypeEnum, StatusValue, TagColor } from './status-tag.model';

const getPheromoneStatusColor = (status: string): TagColor => {
  const expireDays = Number.parseFloat(status);
  if (Number.isNaN(expireDays)) return TagColor.DEFAULT;

  if (expireDays < 1) return TagColor.ERROR;
  if (expireDays >= 1 && expireDays < 10) return TagColor.WARNING;
  return TagColor.SUCCESS;
};

const getStatusColorMap = (positiveStatus?: boolean): Record<string, TagColor> => ({
  [StatusValue.HIGH]: positiveStatus ? TagColor.SUCCESS : TagColor.ERROR,
  [StatusValue.MEDIUM]: TagColor.WARNING,
  [StatusValue.LOW]: positiveStatus ? TagColor.ERROR : TagColor.SUCCESS,
  [StatusValue.EMPTY]: TagColor.DEFAULT,
  [StatusValue.GOOD]: TagColor.SUCCESS,
  [StatusValue.AVERAGE]: TagColor.WARNING,
  [StatusValue.TRUE]: TagColor.SUCCESS,
  [StatusValue.FALSE]: TagColor.ERROR,
  [StatusValue.NO_DATA]: TagColor.DEFAULT,
  [StatusValue.LESS_THAN_5]: TagColor.ERROR,
  [StatusValue.FIVE_TO_TEN]: TagColor.WARNING,
  [StatusValue.GREATER_THAN_10]: TagColor.SUCCESS,
  [StatusValue.SUCCESSFUL]: TagColor.SUCCESS,
  [StatusValue.REJECTED]: TagColor.ERROR,
  [StatusValue.SCHEDULED]: TagColor.WARNING,
  [StatusValue.NO_THRESHOLD]: TagColor.DEFAULT
});

export const mapStatusToColor = (status: Status, type?: StatusTypeEnum, positiveStatus?: boolean): TagColor => {
  if (!status) return TagColor.DEFAULT;

  if (type === StatusTypeEnum.PHEROMONE) {
    return getPheromoneStatusColor(status);
  }

  const statusColorMap = getStatusColorMap(positiveStatus);
  return statusColorMap[status] ?? TagColor.DEFAULT;
};

export const getIconByType = (type?: StatusTypeEnum): ReactNode => {
  switch (type) {
    case StatusTypeEnum.BATTERY:
      return <BatterySaverIcon fontSize={18} />;
    case StatusTypeEnum.SIGNAL:
      return <WifiIcon fontSize={18} />;
    case StatusTypeEnum.PHEROMONE:
      return <PheromoneIcon fontSize={18} />;
    case StatusTypeEnum.TWEAKS:
      return <RotateRightIcon fontSize={18} />;
    case StatusTypeEnum.SELF_CLEANING:
      return <BuildCircleIcon fontSize={18} />;
    default:
      return null;
  }
};

export const getLabelByType = (t: TFunction, type?: StatusTypeEnum, status?: Status): string | undefined => {
  if (!type) return undefined;

  switch (type) {
    case StatusTypeEnum.BATTERY:
    case StatusTypeEnum.SIGNAL:
    case StatusTypeEnum.SELF_CLEANING:
    case StatusTypeEnum.SEVEN_DAY_TOTAL: {
      const validStatusValues = Object.values(StatusValue);
      if (!validStatusValues.includes(status as StatusValue) || !status) {
        return t('pages.regional_overview.digital_traps.no_data');
      }

      return t(`pages.regional_overview.digital_traps.${status}`);
    }

    case StatusTypeEnum.PHEROMONE: {
      const expireDays = Number.parseFloat(status ?? '');

      if (Number.isNaN(expireDays)) return t('pages.regional_overview.digital_traps.not_entered');

      if (expireDays < 1) {
        return t('pages.regional_overview.digital_traps.expired');
      } else {
        return t('pages.regional_overview.digital_traps.expires_in', { value: expireDays });
      }
    }

    case StatusTypeEnum.TWEAKS: {
      if (status === StatusValue.NO_DATA || !status) return t('pages.regional_overview.digital_traps.no_data');

      return status;
    }

    default:
      return t('pages.regional_overview.digital_traps.no_data');
  }
};
